import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { Plus, Edit2, Trash2, X, Copy, Users, Search, Loader2 } from "lucide-react";

const HumanAgentGroups = () => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Group state
    const [groups, setGroups] = useState([]);
    const [humanAgents, setHumanAgents] = useState([]);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupFormData, setGroupFormData] = useState({ name: "", human_agent_ids: [] });

    // Delete confirmation state
    const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    const fetchGroups = async () => {
        setIsLoading(true);
        try {
            const res = await api.request("/api/human-agent-groups");
            if (res && res.groups) {
                setGroups(res.groups);
            }
        } catch (error) {
            console.error("Error fetching agent groups:", error);
            showToast("Failed to load agent groups", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchHumanAgents = async () => {
        try {
            const usersList = await api.request("/api/users");
            if (Array.isArray(usersList)) {
                const filtered = usersList.filter(u => (u.role === "human_agent" || u.role === "admin") && u.is_active !== false);
                setHumanAgents(filtered);
            }
        } catch (error) {
            console.error("Failed to load human agents:", error);
        }
    };

    useEffect(() => {
        fetchGroups();
        fetchHumanAgents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenGroupModal = (group = null) => {
        if (group) {
            setEditingGroup(group);
            const numIds = (group.human_agent_ids || [])
                .map(id => typeof id === "string" ? parseInt(id, 10) : id)
                .filter(id => !isNaN(id));
            setGroupFormData({ name: group.name, human_agent_ids: numIds });
        } else {
            setEditingGroup(null);
            setGroupFormData({ name: "", human_agent_ids: [] });
        }
        setIsGroupModalOpen(true);
    };

    const handleOpenDeleteGroupModal = (group) => {
        setGroupToDelete(group);
        setIsDeleteGroupModalOpen(true);
    };

    const handleGroupSubmit = async (e) => {
        e.preventDefault();

        const normalizedName = groupFormData.name.trim().toLowerCase();
        const isDuplicate = groups.some(g =>
            g.name.trim().toLowerCase() === normalizedName &&
            (!editingGroup || g.id !== editingGroup.id)
        );

        if (isDuplicate) {
            showToast(`A group named "${groupFormData.name}" already exists.`, "error");
            return;
        }

        try {
            const payload = {
                name: groupFormData.name.trim(),
                human_agent_ids: (groupFormData.human_agent_ids || []).map(id => typeof id === "string" ? parseInt(id, 10) : id).filter(id => !isNaN(id))
            };
            if (editingGroup) {
                await api.request(`/api/human-agent-groups/${editingGroup.id}`, {
                    method: "PUT",
                    body: JSON.stringify(payload),
                });
                showToast("Group updated successfully");
            } else {
                await api.request("/api/human-agent-groups", {
                    method: "POST",
                    body: JSON.stringify(payload),
                });
                showToast("Group created successfully");
            }
            setIsGroupModalOpen(false);
            fetchGroups();
        } catch (error) {
            console.error("Error saving group:", error);
            const detail = error?.response?.detail || "Failed to save group";
            showToast(detail, "error");
        }
    };

    const handleDeleteGroup = async () => {
        if (!groupToDelete) return;

        setIsLoading(true);
        try {
            await api.request(`/api/human-agent-groups/${groupToDelete.id}`, {
                method: "DELETE",
            });
            showToast("Group deleted successfully");
            setIsDeleteGroupModalOpen(false);
            setGroupToDelete(null);
            fetchGroups();
        } catch (error) {
            console.error("Error deleting group:", error);
            showToast("Failed to delete group", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredGroups = useMemo(() => {
        if (!searchQuery.trim()) return groups;
        const q = searchQuery.toLowerCase();
        return groups.filter(g =>
            g.name?.toLowerCase().includes(q) ||
            g.group_id?.toLowerCase().includes(q)
        );
    }, [groups, searchQuery]);

    return (
        <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
            <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Operator Groups</h1>
                        <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                            Orchestrate escalation paths and route chat sessions to dedicated operator pools.
                        </p>
                    </div>
                    <button
                        onClick={() => handleOpenGroupModal()}
                        className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] self-start sm:self-auto cursor-pointer"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        <span>Create Group</span>
                    </button>
                </div>

                {/* Search Bar */}
                <div className="flex items-center justify-between gap-3 pt-5 pb-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input
                            type="text"
                            placeholder="Search groups by name or ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 hover:text-zinc-700 font-medium"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Content Area */}
                <div className="mt-2 bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
                    {isLoading && groups.length === 0 ? (
                        <div className="py-20 flex flex-col items-center justify-center text-zinc-400">
                            <Loader2 size={24} className="animate-spin mb-2 text-zinc-600" />
                            <span className="text-xs font-medium">Loading groups...</span>
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="text-center py-16 px-4">
              <h3 className="text-xs font-semibold text-zinc-900">No Groups Found</h3>
                            <p className="text-xs text-zinc-400 mt-1 max-w-xs mx-auto">
                                Create your first operator pool to route chats dynamically.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View (>= md) */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-zinc-50/80 border-b border-zinc-200 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-5 py-3">Group Details</th>
                                            <th className="px-5 py-3">Group Routing ID</th>
                                            <th className="px-5 py-3">Assigned Operators</th>
                                            <th className="px-5 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100 text-xs">
                                        {filteredGroups.map((group) => {
                                            const isDefault = group.name === 'Default Group' || group.group_id === 'default_group';
                                            return (
                                                <tr key={group.id} className="hover:bg-zinc-50/60 transition-colors group">
                                                    <td className="px-5 py-4 align-middle">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-zinc-900 text-xs">{group.name}</span>
                                                            {isDefault && (
                                                                <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 text-[9px] font-semibold border border-zinc-200">
                                                                    Default
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => handleOpenGroupModal(group)}
                                                                className="text-zinc-400 hover:text-zinc-800 p-1 transition-colors"
                                                                title="Edit group"
                                                            >
                                                                <Edit2 size={12} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4 align-middle">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="px-2 py-1 bg-zinc-50 border border-zinc-200/80 rounded-md text-[11px] text-zinc-700 font-medium">
                                                                {group.group_id}
                                                            </span>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(group.group_id);
                                                                    showToast("Group ID copied to clipboard");
                                                                }}
                                                                className="p-1.5 text-zinc-400 hover:text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
                                                                title="Copy Group ID"
                                                            >
                                                                <Copy size={11} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4 align-middle">
                                                        <div className="flex flex-wrap gap-1.5 max-w-sm">
                                                            {group.human_agent_ids && group.human_agent_ids.length > 0 ? (
                                                                group.human_agent_ids.map(id => {
                                                                    const agent = humanAgents.find(a => String(a.id) === String(id));
                                                                    return (
                                                                        <span key={id} className="px-2 py-0.5 bg-zinc-100/70 border border-zinc-200 rounded text-[10px] text-zinc-700 font-medium" title={agent ? agent.email : undefined}>
                                                                            {agent ? (agent.username || agent.human_agent_id) : `ID ${id}`}
                                                                        </span>
                                                                    );
                                                                })
                                                            ) : (
                                                                <span className="text-zinc-400 text-[11px] italic">No operators assigned</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4 align-middle text-right">
                                                        <button
                                                            disabled={isDefault}
                                                            onClick={() => handleOpenDeleteGroupModal(group)}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                isDefault
                                                                    ? "text-zinc-200 cursor-not-allowed"
                                                                    : "text-zinc-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                                                            }`}
                                                            title={isDefault ? "Default group cannot be deleted" : "Delete group"}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View (< md) */}
                            <div className="block md:hidden divide-y divide-zinc-100">
                                {filteredGroups.map((group) => {
                                    const isDefault = group.name === 'Default Group' || group.group_id === 'default_group';
                                    return (
                                        <div key={group.id} className="p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-zinc-900 text-xs">{group.name}</h3>
                                                        {isDefault && (
                                                            <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 text-[9px] font-semibold border border-zinc-200">
                                                                Default
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-1.5">
                                                        <span className="px-2 py-0.5 bg-zinc-50 border border-zinc-200/80 rounded text-[10px] text-zinc-600 font-medium truncate max-w-[200px]">
                                                            {group.group_id}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(group.group_id);
                                                                showToast("Group ID copied");
                                                            }}
                                                            className="p-1 text-zinc-400 hover:text-zinc-700 bg-white border border-zinc-200 rounded"
                                                        >
                                                            <Copy size={10} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button
                                                        onClick={() => handleOpenGroupModal(group)}
                                                        className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-md hover:bg-zinc-100"
                                                        title="Edit group"
                                                    >
                                                        <Edit2 size={13} />
                                                    </button>
                                                    <button
                                                        disabled={isDefault}
                                                        onClick={() => handleOpenDeleteGroupModal(group)}
                                                        className={`p-1.5 rounded-md ${
                                                            isDefault
                                                                ? "text-zinc-200 cursor-not-allowed"
                                                                : "text-zinc-400 hover:text-red-600 hover:bg-red-50"
                                                        }`}
                                                        title={isDefault ? "Default group cannot be deleted" : "Delete group"}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1">
                                                    Operators ({group.human_agent_ids?.length || 0})
                                                </span>
                                                <div className="flex flex-wrap gap-1">
                                                    {group.human_agent_ids && group.human_agent_ids.length > 0 ? (
                                                        group.human_agent_ids.map(id => {
                                                            const agent = humanAgents.find(a => String(a.id) === String(id));
                                                            return (
                                                                <span key={id} className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-[10px] text-zinc-700">
                                                                    {agent ? (agent.username || agent.human_agent_id) : `ID ${id}`}
                                                                </span>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="text-zinc-400 text-[10px] italic">No operators assigned</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Footer */}
                            <div className="px-4 py-3 bg-zinc-50/60 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-400 font-medium">
                                <span>Showing {filteredGroups.length} of {groups.length} groups</span>
                                <span className="text-[10px] text-zinc-400 hidden sm:inline">Session Escalation Pools</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Create/Edit Group Modal */}
            {isGroupModalOpen && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
                    {/* Dark Blurred Backdrop covering everything including Navbar */}
                    <div 
                        className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm transition-opacity animate-fadeIn"
                        onClick={() => setIsGroupModalOpen(false)}
                    />

                    <div className="relative bg-white w-full max-w-md rounded-2xl border border-zinc-200 shadow-2xl overflow-hidden animate-scaleIn z-10 my-auto max-h-[90vh] flex flex-col">
                        <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/60 shrink-0">
                            <div>
                                <h3 className="font-semibold text-zinc-900 text-sm">
                                    {editingGroup ? "Edit Operator Group" : "Create Operator Group"}
                                </h3>
                                <p className="text-[11px] text-zinc-400 mt-0.5">Define name and assign human operators</p>
                            </div>
                            <button
                                onClick={() => setIsGroupModalOpen(false)}
                                className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <form onSubmit={handleGroupSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
                            {editingGroup && (
                                <div>
                                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                                        Group Routing ID
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs text-zinc-700 select-all truncate font-mono">
                                            {editingGroup.group_id}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(editingGroup.group_id);
                                                showToast("Group ID copied");
                                            }}
                                            className="p-2 text-zinc-500 hover:text-zinc-800 bg-zinc-50 border border-zinc-200 rounded-lg hover:bg-zinc-100 transition-colors shrink-0"
                                            title="Copy Group ID"
                                        >
                                            <Copy size={13} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                                    Group Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={groupFormData.name}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                                    placeholder="e.g. Sales Escalation Team"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wider">
                                        Assign Human Operators
                                    </label>
                                    <span className="text-[10px] text-zinc-400 font-medium">
                                        {groupFormData.human_agent_ids.length} selected
                                    </span>
                                </div>
                                <div className="max-h-40 sm:max-h-48 overflow-y-auto border border-zinc-200 rounded-lg p-2 sm:p-2.5 bg-zinc-50/50 space-y-1.5 custom-scrollbar">
                                    {humanAgents.map((agent) => {
                                        const humanAgentIdNum = parseInt(agent.id, 10);
                                        const isChecked = groupFormData.human_agent_ids.includes(humanAgentIdNum);
                                        return (
                                            <label
                                                key={agent.id}
                                                className={`flex items-center gap-3 cursor-pointer p-2 rounded-lg border transition-all ${
                                                    isChecked
                                                        ? "bg-white border-zinc-300 shadow-2xs"
                                                        : "border-transparent hover:bg-white"
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {
                                                        if (isChecked) {
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                human_agent_ids: groupFormData.human_agent_ids.filter(id => id !== humanAgentIdNum)
                                                            });
                                                        } else {
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                human_agent_ids: [...groupFormData.human_agent_ids, humanAgentIdNum]
                                                            });
                                                        }
                                                    }}
                                                    className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-3.5 h-3.5"
                                                />
                                                <div className="flex items-center justify-between w-full min-w-0">
                                                    <span className="text-xs font-semibold text-zinc-900 truncate">
                                                        {agent.username}
                                                    </span>
                                                    <span className="text-[10px] text-zinc-400 truncate ml-2">
                                                        {agent.email}
                                                    </span>
                                                </div>
                                            </label>
                                        );
                                    })}
                                    {humanAgents.length === 0 && (
                                        <p className="text-xs text-zinc-400 text-center py-4">No human agents available.</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-zinc-100 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setIsGroupModalOpen(false)}
                                    className="px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900 font-medium cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-zinc-900 text-white font-semibold text-xs rounded-lg hover:bg-zinc-800 transition-all shadow-xs cursor-pointer active:scale-[0.99]"
                                >
                                    {editingGroup ? "Save Changes" : "Create Group"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            <DeleteConfirmModal
                isOpen={isDeleteGroupModalOpen}
                onClose={() => setIsDeleteGroupModalOpen(false)}
                onConfirm={handleDeleteGroup}
                isLoading={isLoading}
                title="Delete Operator Group"
                message={`This action will delete group '${groupToDelete?.name}'. The operators in this group will remain untouched.`}
                confirmText="Delete Group"
            />
        </div>
    );
};

export default HumanAgentGroups;
